import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  EvidenceScope,
  EvidenceStatus,
  FileAttachmentIntake,
  FileStatus,
  ProvidedEvidenceIntake,
  RequirementIntake,
} from 'api';
import { DescriptionComponent } from 'common/description/description.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  RequirementIntakeCommandService,
  UploadEvidenceDocumentResult,
} from 'component/details/requirement-intake/requirement-intake-command.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FileStatusComponent } from 'component/details/requirement-intake/requirements-detail/evidence-slots/evidence-slot-card/file-status.component';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { MatDialog } from '@angular/material/dialog';
import { ReuseEvidenceDialogComponent } from 'component/details/requirement-intake/requirements-detail/evidence-slots/evidence-reuse/reuse-evidence-dialog.component';
import { FileProperties } from 'common/files/file-properties';
import { ButtonDirective } from 'common/button/button.directive';

export interface EvidenceIntake {
  id: number;
  name: string;
  status: EvidenceStatus;
  scope: EvidenceScope;
  type: string;
  description: string;
  link?: string;
  fileAttachments: Array<FileAttachmentIntake>;
}

interface InteractionMessage {
  readonly message: string;
  readonly isError: boolean;
}

@Component({
  selector: 'comp-evidence-slot-card',
  templateUrl: './evidence-slot-card.component.html',
  styleUrls: ['./evidence-slot-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DescriptionComponent, TranslateModule, FileStatusComponent, AutofocusDirective,
    ButtonDirective,],
})
export class EvidenceSlotCardComponent {
  private readonly dialog = inject(MatDialog);
  private readonly commandService = inject(RequirementIntakeCommandService);
  private readonly translateService = inject(TranslateService);
  readonly evidence = input.required<EvidenceIntake>();
  readonly requirement = input.required<RequirementIntake | undefined>();
  readonly isOtherSlot = input<boolean>(false);
  readonly isProvided = input<boolean>(false);
  readonly currentUpload = signal<FileProperties | undefined>(undefined);
  readonly uploadPercentage = signal<number>(0);

  readonly uploadSucceeded = output<{
    evidenceId: number;
    file: File;
    uploadResult: UploadEvidenceDocumentResult;
  }>();
  readonly otherEvidenceCreated = output<ProvidedEvidenceIntake>();
  readonly reuseRequested = output<number>();
  readonly notProvidedMarked = output<number>();
  readonly notifyReloadNeeded = output<number>();

  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly isTextEntryOpen = signal(false);
  protected readonly textEvidenceDraft = signal('');
  protected readonly isSkipped = signal(false);
  protected readonly isDragActive = signal(false);

  protected readonly isAddAnotherFileOpen = signal(true);
  protected readonly interactionMessage = signal<InteractionMessage | undefined>(undefined);

  private readonly MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
  INITIAL_OTHER_EVIDENCE_ID = -1;

  protected readonly isConfirmReuse = computed(() => {
    const ev = this.evidence();
    return ev?.status === EvidenceStatus.ConfirmReuse;
  });

  protected readonly isFulfilled = computed(() => {
    const ev = this.evidence();
    return (
      ev !== undefined &&
      ev.status !== EvidenceStatus.Missing &&
      ev.status !== EvidenceStatus.NotProvided
    );
  });

  constructor() {
    effect(() => {
      this.isAddAnotherFileOpen.set(!this.isOtherSlot() && !this.isFulfilled());
    });
    effect(() => {
      if (this.evidence().status === EvidenceStatus.NotProvided && !this.isOtherSlot()) {
        this.isSkipped.set(true);
      }
    });
  }

  protected requestUpload(): void {
    if (this.currentUpload()) {
      return;
    }

    this.fileInput().nativeElement.click();
  }

  protected onDropzoneKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.requestUpload();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();

    if (this.currentUpload()) {
      return;
    }

    this.isDragActive.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragActive.set(false);
  }

  protected async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isDragActive.set(false);

    if (this.currentUpload()) {
      return;
    }

    this.uploadFile(event.dataTransfer?.files?.[0]);
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.uploadFile(file);
    }

    input.value = '';
  }

  private async uploadFile(file?: File): Promise<void> {
    this.interactionMessage.set(undefined);

    if (!file) {
      return;
    }

    if (file.size > this.MAX_UPLOAD_BYTES) {
      this.showErrorMessage('requirementIntake.evidence.messages.fileTooLarge', {
        size: this.MAX_UPLOAD_BYTES / (1024 * 1024),
      });
      return;
    }

    if (!Number.isInteger(this.evidence().id)) {
      this.showErrorMessage('requirementIntake.evidence.messages.uploadListedOnly');
      return;
    }

    this.isAddAnotherFileOpen.set(false);
    let evidenceIdToUse =
      this.isOtherSlot() && this.evidence().id === this.INITIAL_OTHER_EVIDENCE_ID
        ? await this.createNewOtherEvidence()
        : this.evidence().id;

    try {
      this.currentUpload.set({
        filename: file.name,
        fileStatus: FileStatus.Uploading,
        confirmReuse: false,
      });
      const uploadResult = await firstValueFrom(
        this.commandService.uploadEvidenceDocument(evidenceIdToUse, file, (percentage) => {
          this.uploadPercentage.set(percentage);
        }),
      );
      this.uploadSucceeded.emit({ evidenceId: evidenceIdToUse, file, uploadResult });
      this.interactionMessage.set(undefined);
    } catch (error) {
      this.isAddAnotherFileOpen.set(true);
      const message =
        error instanceof HttpErrorResponse && !!error.error
          ? error.error.message
          : error instanceof Error
            ? error.message
            : String(error);
      this.interactionMessage.set({
        message: this.translateService.instant('requirementIntake.evidence.messages.uploadFailed', {
          message,
        }),
        isError: true,
      });
    } finally {
      this.uploadPercentage.set(0);
      this.currentUpload.set(undefined);
    }
  }

  protected markNotProvided(): void {
    this.isTextEntryOpen.set(false);
    this.textEvidenceDraft.set('');
    if (this.isOtherSlot()) {
      this.isAddAnotherFileOpen.set(false);
    } else {
      const evidenceId = this.evidence().id;
      this.commandService.markEvidenceNotProvided(evidenceId).subscribe({
        next: () => {
          this.isSkipped.set(true);
          this.notProvidedMarked.emit(this.evidence().id);
        },
        error: (error: HttpErrorResponse) => {
          this.showErrorMessage('requirementIntake.evidence.messages.markNotProvidedFailed', {
            name: this.evidence().name,
            message: error.message,
          });
        },
      });
    }
  }

  protected undoSkip(): void {
    this.isSkipped.set(false);
  }

  protected openTextEvidence(): void {
    this.isTextEntryOpen.set(true);
  }

  toggleAnotherFileForm(): void {
    this.isAddAnotherFileOpen.set(!this.isAddAnotherFileOpen());
    if (this.isAddAnotherFileOpen()) {
      this.interactionMessage.set(undefined);
    }
  }

  protected cancelTextEvidence(): void {
    this.isTextEntryOpen.set(false);
    this.textEvidenceDraft.set('');
  }

  protected saveTextEvidence(): void {
    const text = this.textEvidenceDraft().trim();
    if (text.length === 0) {
      return;
    }

    const textEvidenceFile = new File([text], 'plaintext.txt', { type: 'text/plain' });
    this.uploadFile(textEvidenceFile);
    this.textEvidenceDraft.set('');
    this.isTextEntryOpen.set(false);
  }

  protected showErrorMessage(messageKey: string, interpolateParams?: Object): void {
    this.interactionMessage.set({
      message: this.translateService.instant(messageKey, interpolateParams),
      isError: true,
    });
  }

  private async createNewOtherEvidence(): Promise<number> {
    const otherEvidence = await firstValueFrom(
      this.commandService.createOtherEvidence(this.requirement()!.id!),
    );
    this.otherEvidenceCreated.emit(otherEvidence);
    return otherEvidence.id;
  }

  protected handleDelete($event: { fileAttachmentId: number; file: FileAttachmentIntake }) {
    const { fileAttachmentId, file } = $event;
    this.commandService.deleteEvidenceDocument(fileAttachmentId).subscribe({
      next: () => {
        this.notifyReloadNeeded.emit(this.requirement()!.id);
      },
      error: (error: HttpErrorResponse) => {
        this.showErrorMessage('requirementIntake.evidence.messages.deletionFailed', {
          name: file.filename,
          message: error.message,
        });
      },
    });
  }

  protected async openReuseDialog() {
    const detailsDialogRef = this.dialog.open<ReuseEvidenceDialogComponent>(
      ReuseEvidenceDialogComponent,
      {
        data: {
          evidence: this.evidence(),
        },
      },
    );
    const dialogResult = await firstValueFrom(detailsDialogRef.afterClosed());
    if (dialogResult) {
      this.handleReuseConfirmed();
    }
  }

  protected handleReuseConfirmed() {
    this.notifyReloadNeeded.emit(this.requirement()!.id);
  }
}
